<template>
  <section class="return-addresses">
    <div class="return-addresses-heading">
      <div>
        <h3 class="text-h6">Default return addresses</h3>
        <p class="mt-1 mb-0">Choose a default return address for each country.</p>
      </div>
      <v-btn color="primary" small :disabled="!editable" @click="openPicker()">+ Add country</v-btn>
    </div>
    <div class="return-addresses-scroll">
      <table class="return-addresses-table">
        <thead><tr><th>Country</th><th>Default return-to address</th><th>Actions</th></tr></thead>
        <tbody>
          <tr v-for="(address, index) in value" :key="address.country_code || address.country">
            <td>
              <div>{{ countryName(address) }}</div>
              <div class="return-addresses-meta">{{ address.country_code }}</div>
            </td>
            <td>
              <div class="return-addresses-meta">COMPANY ID: {{ address.c_custno }}</div>
              <strong>{{ address.c_company }}</strong>
              <div v-if="address.c_contact && address.c_contact.trim()">{{ address.c_contact }}</div>
              <div v-if="address.c_address1">{{ address.c_address1 }}</div>
              <div v-if="address.c_address2">{{ address.c_address2 }}</div>
              <div>{{ locality(address) }}</div>
              <div>{{ address.c_country }}</div>
            </td>
            <td class="return-addresses-actions">
              <v-btn text small color="primarytext" :disabled="!editable" @click="openPicker(index)">Change</v-btn>
              <v-btn text small color="primarytext" :disabled="!editable" @click="remove(index)">Remove</v-btn>
            </td>
          </tr>
          <tr v-if="!value.length"><td colspan="3" class="text-center py-8">No default return addresses. Add a country to choose one.</td></tr>
        </tbody>
      </table>
    </div>
    <p class="text-caption mt-4 mb-0">One default return address per country.</p>
    <v-dialog v-model="picker" max-width="620">
      <v-card class="containerbox">
        <v-card-title>{{ editingIndex === null ? 'Add country' : 'Change return-to address' }}</v-card-title>
        <v-card-text>
          <v-alert v-if="loadError" type="error" dense>{{ loadError }}</v-alert>
          <v-autocomplete v-model="selectedCountry" :items="availableCountries" item-text="label" item-value="country"
            label="Country" outlined dense :loading="loading" :disabled="loading || editingIndex !== null"
            @change="selectedCompany = null" />
          <v-autocomplete v-model="selectedCompany" :items="availableCompanies" item-text="custno" item-value="comp_id"
            label="Company ID" outlined dense :loading="loading" :disabled="loading || !selectedCountry" />
          <div v-if="chosenCompany" class="return-addresses-preview">
            <strong>{{ chosenCompany.company }}</strong>
            <div>{{ chosenCompany.address1 }}</div>
            <div v-if="chosenCompany.address2">{{ chosenCompany.address2 }}</div>
            <div>{{ locality(chosenCompany) }}</div>
            <div>{{ chosenCompany.country }}</div>
          </div>
          <p v-if="!loading && !loadError && !availableCompanies.length" class="mb-0">
            {{ selectedCountry ? 'No return-to companies are available for this country.' : 'Choose a country with an available return-to company.' }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="picker = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!chosenCompany || loading || !editable" @click="apply">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import Utility from '../../shared/utility';

export default {
  props: {
    value: { type: Array, default: () => [] },
    editable: Boolean,
    projectKey: [String, Number],
    businessKey: [String, Number],
    userId: [String, Number],
  },
  data: () => ({
    picker: false, loading: false, loadError: '', editingIndex: null,
    selectedCountry: null, selectedCompany: null, companies: [], countries: [],
  }),
  computed: {
    availableCountries() {
      const countries = new Map();
      this.companies.forEach(company => {
        if (!company.country) return;
        const country = this.countryDetails(company.country);
        const used = this.value.some((address, index) => index !== this.editingIndex &&
          this.countryDetails(address.country).country === country.country);
        if (!used) countries.set(country.country, country);
      });
      if (this.editingIndex !== null) {
        const country = this.countryDetails(this.value[this.editingIndex].country);
        countries.set(country.country, country);
      }
      return Array.from(countries.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
    availableCompanies() {
      return this.companies.filter(company => this.countryDetails(company.country).country === this.selectedCountry);
    },
    chosenCompany() {
      return this.availableCompanies.find(company => String(company.comp_id) === String(this.selectedCompany));
    },
  },
  methods: {
    countryDetails(country) {
      const key = String(country || '').trim().toUpperCase();
      const details = this.countries.find(item => [item.Country, item.code_2, item.code_3]
        .some(value => value && String(value).trim().toUpperCase() === key));
      return { country: details ? details.Country : country, code: details ? details.code_2 : '', label: details ? details.Country : country };
    },
    countryName(address) {
      if (address.country_code && typeof Intl.DisplayNames === 'function') {
        try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(address.country_code.toUpperCase()); }
        catch (error) { /* Use the API's country name when a code is invalid. */ }
      }
      return address.country;
    },
    locality(address) {
      return [address.c_city || address.city, [address.c_state || address.state, address.c_zip || address.Zip || address.zip].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ');
    },
    async openPicker(index = null) {
      if (!this.editable) return;
      this.editingIndex = index;
      this.selectedCountry = index === null ? null : this.countryDetails(this.value[index].country).country;
      this.selectedCompany = index === null ? null : this.value[index].c_comp_id;
      this.loadError = '';
      this.picker = true;
      this.loading = true;
      try {
        const [companies, countries] = await Promise.all([
          this.axios.get(`/cp/company_details?pass=4&user_id=${this.userId}&proj_key=${this.projectKey}&bu_key=${this.businessKey}`),
          this.axios.post('/co/country_state_master', {
            user_id: parseInt(this.userId), pass: 1,
            CountryJSON: JSON.stringify({ country_id: 0, country_name: '', country_code2: '', country_code3: '', phone_format: '', state_code: '', state_name: '' }),
          }),
        ]);
        this.companies = JSON.parse(companies.data.body.message).CompanyInfo || [];
        this.countries = JSON.parse(countries.data.body.message) || [];
        if (index !== null) this.selectedCountry = this.countryDetails(this.value[index].country).country;
      } catch (error) {
        this.companies = [];
        this.loadError = Utility.apiError(error);
      } finally {
        this.loading = false;
      }
    },
    apply() {
      if (!this.editable || !this.chosenCompany) return;
      const company = this.chosenCompany;
      const existing = this.editingIndex === null ? {} : this.value[this.editingIndex];
      const country = this.countryDetails(company.country);
      const address = { ...existing, country: country.country, country_code: country.code || existing.country_code || '', c_comp_id: company.comp_id, c_custno: company.custno,
        country_id: existing.country_id != null ? existing.country_id : company.country_id,
        c_company: company.company, c_address1: company.address1, c_address2: company.address2,
        c_city: company.city, c_state: company.state, c_zip: company.Zip || company.zip,
        c_country: company.country, c_contact: company.ContName, c_title: company.title,
        c_email: company.email, c_phone1: company.phone1, c_phone2: company.phone2, c_cell: company.cell, c_fax: company.fax };
      const addresses = this.value.slice();
      if (this.editingIndex === null) addresses.push(address);
      else addresses.splice(this.editingIndex, 1, address);
      this.$emit('input', addresses);
      this.picker = false;
    },
    remove(index) {
      if (this.editable) this.$emit('input', this.value.filter((address, itemIndex) => index !== itemIndex));
    },
  },
};
</script>

<style scoped>
.return-addresses { padding: 8px 0 24px; }
.return-addresses-heading { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin-bottom: 28px; }
.return-addresses-scroll { overflow-x: auto; }
.return-addresses-table { width: 100%; border-collapse: collapse; text-align: left; }
.return-addresses-table th { background: rgba(127, 140, 155, .13); font-weight: 400; padding: 12px; }
.return-addresses-table td { padding: 20px 12px; vertical-align: top; line-height: 1.6; }
.return-addresses-table tr { border-bottom: 1px solid rgba(127, 140, 155, .4); }
.return-addresses-table th:first-child { width: 23%; }
.return-addresses-table th:last-child { width: 190px; }
.return-addresses-meta { font-size: 11px; letter-spacing: .4px; }
.return-addresses-actions { white-space: nowrap; }
.return-addresses-actions .v-btn { text-transform: none; letter-spacing: normal; font-size: 14px; min-width: 0; padding: 0 8px; }
.return-addresses-preview { line-height: 1.6; }
@media (max-width: 600px) {
  .return-addresses-heading { align-items: flex-start; flex-direction: column; }
  .return-addresses-table { min-width: 560px; }
}
</style>
